import { useState, useEffect } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { useAuth } from '../context/AuthContext';
import { 
  Crown,
  Loader2,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Users,
  DollarSign,
  Calendar,
  UserMinus,
  UserPlus,
  Clock,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from '../lib/toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MetricCard = ({ title, value, change, changeLabel, icon: Icon, color = 'blue', prefix = '', suffix = '' }) => {
  const isPositive = change > 0;
  const isNeutral = change === 0;
  
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm text-gray-500 font-medium">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">
            {prefix}{typeof value === 'number' ? value.toLocaleString(undefined, { minimumFractionDigits: prefix === '$' ? 2 : 0, maximumFractionDigits: 2 }) : value}{suffix}
          </p>
          {change !== undefined && (
            <div className={`flex items-center gap-1 mt-2 text-xs font-medium ${isNeutral ? 'text-gray-500' : isPositive ? 'text-green-600' : 'text-red-600'}`}>
              {!isNeutral && (isPositive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />)}
              <span>{isPositive ? '+' : ''}{change}{suffix}</span>
              {changeLabel && <span className="text-gray-400 font-normal">vs {changeLabel}</span>}
            </div>
          )}
        </div>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
          color === 'amber' ? 'bg-amber-100' :
          color === 'green' ? 'bg-green-100' :
          color === 'red' ? 'bg-red-100' :
          color === 'purple' ? 'bg-purple-100' :
          'bg-blue-100'
        }`}>
          <Icon className={`w-5 h-5 ${
            color === 'amber' ? 'text-amber-600' :
            color === 'green' ? 'text-green-600' :
            color === 'red' ? 'text-red-600' :
            color === 'purple' ? 'text-purple-600' :
            'text-blue-600'
          }`} />
        </div>
      </div>
    </div>
  );
};

const ChurnRing = ({ rate }) => {
  const circumference = 2 * Math.PI * 40;
  const strokeDashoffset = circumference - (rate / 100) * circumference;
  const color = rate < 5 ? '#22c55e' : rate < 10 ? '#f59e0b' : '#ef4444';
  
  return (
    <div className="relative w-24 h-24">
      <svg className="w-24 h-24 transform -rotate-90">
        <circle
          cx="48"
          cy="48"
          r="40"
          stroke="#e5e7eb"
          strokeWidth="8"
          fill="none"
        />
        <circle
          cx="48"
          cy="48"
          r="40"
          stroke={color}
          strokeWidth="8"
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-lg font-bold text-gray-900">{rate.toFixed(1)}%</span>
      </div>
    </div>
  );
};

const SubscriberRow = ({ subscriber }) => {
  const statusColors = {
    active: 'bg-green-100 text-green-700',
    trialing: 'bg-blue-100 text-blue-700',
    past_due: 'bg-amber-100 text-amber-700',
    canceled: 'bg-red-100 text-red-700'
  };
  
  return (
    <tr className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
            <Crown className="w-4 h-4 text-amber-600" />
          </div>
          <div>
            <p className="font-medium text-gray-900">{subscriber.username}</p>
            <p className="text-xs text-gray-500">{subscriber.email}</p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[subscriber.status] || 'bg-gray-100 text-gray-700'}`}>
          {subscriber.status}
        </span>
      </td>
      <td className="px-4 py-3 text-sm text-gray-600">
        {subscriber.subscribed_at ? new Date(subscriber.subscribed_at).toLocaleDateString('en-AU') : '-'}
      </td>
      <td className="px-4 py-3 text-sm text-gray-600">
        {subscriber.current_period_end ? new Date(subscriber.current_period_end).toLocaleDateString('en-AU') : '-'}
      </td>
      <td className="px-4 py-3">
        {subscriber.cancel_at_period_end ? (
          <span className="text-xs text-red-600 font-medium">Canceling</span>
        ) : (
          <span className="text-xs text-green-600 font-medium">Active</span>
        )}
      </td>
    </tr>
  );
};

export default function AdminSubscriptionsPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [timeRange, setTimeRange] = useState('30d');

  const fetchSubscriptionData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/analytics/subscriptions?range=${timeRange}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Admin access required');
      } else {
        toast.error('Failed to load subscription data');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscriptionData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, timeRange]);

  if (loading && !data) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Crown className="w-6 h-6 text-amber-500" />
              WANTD+ Subscription Analytics
            </h1>
            <p className="text-gray-500">Monitor subscription performance and growth</p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="all">All time</option>
            </select>
            <Button onClick={fetchSubscriptionData} variant="outline" className="gap-2" disabled={loading}>
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* MRR Banner */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl p-6 mb-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="w-5 h-5 text-amber-100" />
                <span className="text-amber-100 font-medium">Monthly Recurring Revenue (MRR)</span>
              </div>
              <p className="text-4xl font-bold">
                ${(data?.mrr || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
              <p className="text-amber-200 text-sm mt-2">
                {data?.active_subscribers || 0} active subscribers × $5.00/month
              </p>
            </div>
            <div className="text-right">
              <p className="text-amber-100 text-sm mb-1">Projected ARR</p>
              <p className="text-2xl font-bold">
                ${((data?.mrr || 0) * 12).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <MetricCard
            title="Active Subscribers"
            value={data?.active_subscribers || 0}
            change={data?.subscriber_change || 0}
            changeLabel="last period"
            icon={Users}
            color="amber"
          />
          <MetricCard
            title="New Subscriptions"
            value={data?.new_subscriptions || 0}
            change={data?.new_sub_change}
            changeLabel="last period"
            icon={UserPlus}
            color="green"
          />
          <MetricCard
            title="Churned"
            value={data?.churned || 0}
            change={data?.churn_change}
            changeLabel="last period"
            icon={UserMinus}
            color="red"
          />
          <MetricCard
            title="Avg Lifetime"
            value={data?.avg_lifetime_days || 0}
            suffix=" days"
            icon={Clock}
            color="blue"
          />
        </div>

        {/* Churn & Growth */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Churn Rate */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
              <TrendingDown className="w-5 h-5 text-red-500" />
              Churn Rate
            </h3>
            <div className="flex items-center justify-center py-4">
              <ChurnRing rate={data?.churn_rate || 0} />
            </div>
            <p className="text-center text-sm text-gray-500 mt-2">
              {data?.churn_rate < 5 ? 'Excellent retention!' : 
               data?.churn_rate < 10 ? 'Room for improvement' : 
               'Needs attention'}
            </p>
            <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-2 gap-4 text-center">
              <div>
                <p className="text-xs text-gray-500">Canceled</p>
                <p className="text-lg font-bold text-gray-900">{data?.canceled_count || 0}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Past Due</p>
                <p className="text-lg font-bold text-amber-600">{data?.past_due_count || 0}</p>
              </div>
            </div>
          </div>

          {/* Growth Trend */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm lg:col-span-2">
            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-green-500" />
              Subscription Growth
            </h3>
            {data?.monthly_growth && data.monthly_growth.length > 0 ? (
              <div className="space-y-3">
                {data.monthly_growth.map((month, idx) => (
                  <div key={idx} className="flex items-center gap-4">
                    <span className="text-sm text-gray-500 w-16">{month.month}</span>
                    <div className="flex-1 h-8 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-amber-400 to-amber-500 rounded-full flex items-center justify-end pr-3"
                        style={{ width: `${Math.max(10, (month.count / (data.active_subscribers || 1)) * 100)}%` }}
                      >
                        <span className="text-xs font-bold text-white">{month.count}</span>
                      </div>
                    </div>
                    {month.change !== undefined && (
                      <span className={`text-xs font-medium w-12 text-right ${month.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {month.change >= 0 ? '+' : ''}{month.change}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-gray-400">
                <BarChart3 className="w-12 h-12 mb-3 opacity-50" />
                <p className="text-sm">No growth data available yet</p>
              </div>
            )}
          </div>
        </div>

        {/* Subscriber List */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="font-bold text-gray-900 flex items-center gap-2">
              <Crown className="w-5 h-5 text-amber-500" />
              Active Subscribers
            </h3>
          </div>
          
          {data?.subscribers && data.subscribers.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                    <th className="px-4 py-3 font-medium">Subscriber</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Subscribed</th>
                    <th className="px-4 py-3 font-medium">Renews</th>
                    <th className="px-4 py-3 font-medium">State</th>
                  </tr>
                </thead>
                <tbody>
                  {data.subscribers.map((sub, idx) => (
                    <SubscriberRow key={idx} subscriber={sub} />
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <Crown className="w-12 h-12 mb-3 opacity-50" />
              <p className="text-sm">No subscribers yet</p>
              <p className="text-xs mt-1">Subscribers will appear here once users upgrade to WANTD+</p>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
